import { useQuery } from '@apollo/client';
import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DynamicResultTable,
  SupportIssueStatusLozenge,
  FormattedDate,
  SupportIssueStatusFilter,
  DateFilter,
} from '@css-xp/base';
import {
  DateRange,
  FetchSupportIssuesForEntitlementQuery,
  FetchSupportIssuesForEntitlementQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
// @ts-ignore
import SupportIssueIcon from '@css-xp/base/assets/img/icon-support-issue.svg';
import { SUPPORT_ISSUE_PATH, NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { SupportIssueStatusEnum } from '@atlassian/cssxp-types-support';
import { DEFAULT_ROWS_PER_PAGE, DEFAULT_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { fetchSupportIssuesForEntitlement as SupportIssueQuery } from '../../queries/fetch-entitlement-support-issue';

export interface SupportTabProps {
  id: string;
  showFilters?: boolean;
}

export const SupportTab = React.memo(({ id, showFilters = false }: SupportTabProps) => {
  const [dateRange, setDateRange] = useState<DateRange>({ from: '', to: '' });
  const [selectedStatus, setSelectedStatus] = useState<SupportIssueStatusEnum[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);

  const { data, loading } = useQuery<
    FetchSupportIssuesForEntitlementQuery,
    FetchSupportIssuesForEntitlementQueryVariables
  >(SupportIssueQuery, {
    variables: {
      id: id,
      searchOptions: { first: rowsPerPage, offset: currentPage !== 1 ? currentPage * rowsPerPage - rowsPerPage : 0 },
      searchParam: {
        updatedFrom: dateRange.from ? dateRange.from : null,
        updatedTo: dateRange.to ? dateRange.to : null,
        currentStatusIsIn: selectedStatus.length ? selectedStatus : null,
      },
    },
    displayName: `fetchSupportIssuesForEntitlement`,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !id,
  });

  const results = data?.entitlementDetails;
  const supportIssuesConnection = results?.supportIssues;
  const supportIssuesEdges = supportIssuesConnection?.results || [];
  const totalHits = supportIssuesConnection?.totalHits ?? 0;

  const headData = useMemo(() => {
    const cells = [
      {
        content: 'Issue key',
        width: 10,
      },
      {
        content: 'Summary',
        width: 40,
      },
      {
        content: 'Status',
        width: 15,
      },
      {
        content: 'Reporter',
        width: 25,
      },
      {
        content: 'Updated',
        width: 10,
      },
    ];

    return {
      cells,
    };
  }, []);

  const emptyStateOrderProp = {
    header: '',
    description: 'No support issues found',
  };

  const createRows = (): Array<any> => {
    const result = (supportIssuesEdges ?? []).map((supportIssue) => {
      const { issueKey, updatedTimestamp, summary, reporterEmailAddress, currentStatus } = supportIssue ?? {};
      return {
        key: issueKey,
        cells: [
          {
            content: (
              <>
                {/*@ts-ignore*/}
                <img src={SupportIssueIcon} alt="support issue icon" className="support-icon" />
                <Link to={`${SUPPORT_ISSUE_PATH}/${issueKey}`} data-testid="support-issue-link" target="_blank">
                  {issueKey}
                </Link>
              </>
            ),
          },
          {
            content: summary,
          },
          {
            content: <SupportIssueStatusLozenge status={currentStatus} />,
          },
          {
            content: reporterEmailAddress,
          },
          {
            content: updatedTimestamp ? <FormattedDate date={updatedTimestamp} format={NEW_DATE_FORMAT} /> : 'N/A',
          },
        ],
      };
    });
    return result;
  };

  const getDateRange = useCallback((value: any) => {
    value.to = new Date(value.to / 1000).toISOString();
    value.from = new Date(value.from / 1000).toISOString();
    setDateRange(value);
  }, []);

  const handleRowsChange = useCallback((rowsCount: number) => {
    setRowsPerPage(rowsCount);
    setCurrentPage(1);
  }, []);

  const onPageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const onStatusChange = useCallback((value) => {
    setSelectedStatus(Object.keys(value) as SupportIssueStatusEnum[]);
  }, []);

  return (
    <div className="support-issues-tab" data-testid={`entitlement-support-issues-tab`}>
      <DynamicResultTable
        testId={`entitlement-support-issues`}
        headingText="Support tickets"
        head={headData}
        rows={createRows()}
        emptyStateProps={emptyStateOrderProp}
        isLoading={loading}
        totalCount={totalHits}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        isPaginationEnabled={supportIssuesEdges && supportIssuesEdges?.length > 0 && totalHits > DEFAULT_ROWS_PER_PAGE}
        onRowChange={handleRowsChange}
        showFilters={showFilters}
      >
        <div className="support-filters">
          <SupportIssueStatusFilter onChange={onStatusChange} />
          <DateFilter dateValue={getDateRange} />
        </div>
      </DynamicResultTable>
    </div>
  );
});
